import { useState } from 'react'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ActionIcon,
  Collapse,
  Loader,
  Menu,
  Tooltip,
} from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  ARCHIVE_CUSTOM_FIELD,
  GET_CUSTOM_FIELDS,
  REORDER_CUSTOM_FIELDS,
} from 'src/graphql/customFields.graphql'
import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconClock from 'src/icons/IconClock'
import IconCopy from 'src/icons/IconCopy'
import IconEdit from 'src/icons/IconEdit'
import IconThreeDots from 'src/icons/IconThreeDots'
import IconTrash from 'src/icons/IconTrash'

import ChangeHistoryDrawer from './ChangeHistoryDrawer'
import CustomFieldModal from './CustomFieldModal'
import { getFieldTypeLabel } from './customFieldsUtils'

// Drag handle icon component
const DragHandleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="6" cy="4" r="1.5" />
    <circle cx="10" cy="4" r="1.5" />
    <circle cx="6" cy="8" r="1.5" />
    <circle cx="10" cy="8" r="1.5" />
    <circle cx="6" cy="12" r="1.5" />
    <circle cx="10" cy="12" r="1.5" />
  </svg>
)

// Sortable table row component
const SortableTableRow = ({
  field,
  index,
  totalCount,
  onEdit,
  onArchive,
  isReordering,
  onCopyId,
  onShowHistory,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.customFieldId, disabled: isReordering })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${
        index !== totalCount - 1 ? 'border-b border-doubleNickel-gray-200' : ''
      }`}
    >
      <td className="w-8 px-2 py-3">
        {isReordering ? (
          <div className="flex items-center justify-center">
            <Loader size="xs" color="gray" />
          </div>
        ) : (
          <button
            className="cursor-grab text-doubleNickel-gray-400 hover:text-doubleNickel-gray-600 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isReordering}
            {...attributes}
            {...listeners}
          >
            <DragHandleIcon />
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <Tooltip label={field.customFieldId} withArrow>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            onClick={(e) => {
              e.stopPropagation()
              onCopyId(field.customFieldId)
            }}
          >
            <IconCopy className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
      </td>
      <td className="px-4 py-3 text-sm text-doubleNickel-gray-900">
        <Tooltip label={field.label} disabled={field.label.length <= 30}>
          <div className="max-w-[200px] truncate">{field.label}</div>
        </Tooltip>
        {field.options?.length > 0 && (
          <div className="mt-1 text-xs text-doubleNickel-gray-500">
            {field.options.length} option
            {field.options.length !== 1 ? 's' : ''}
          </div>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-sm text-doubleNickel-gray-600">
        <Tooltip label={field.key} disabled={field.key.length <= 20}>
          <div className="max-w-[150px] truncate">{field.key}</div>
        </Tooltip>
      </td>
      <td className="px-4 py-3 text-sm text-doubleNickel-gray-700">
        {getFieldTypeLabel(field.fieldType)}
      </td>
      <td className="px-4 py-3 text-center text-sm">
        {field.isRequired ? (
          <span className="text-doubleNickel-success-600">✓</span>
        ) : (
          <span className="text-doubleNickel-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-center text-sm">
        {field.isSearchable ? (
          <span className="text-doubleNickel-success-600">✓</span>
        ) : (
          <span className="text-doubleNickel-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        <Menu
          withinPortal
          position="bottom-end"
          classNames={{
            item: 'pl-0',
          }}
        >
          <Menu.Target>
            <ActionIcon variant="subtle" size="sm">
              <IconThreeDots className="h-4 w-4" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit className="h-4 w-4 fill-none" />}
              onClick={() => onEdit(field)}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={<IconClock className="h-4 w-4 fill-none" />}
              onClick={() => onShowHistory(field)}
            >
              Change History
            </Menu.Item>
            <Menu.Item
              leftSection={<IconTrash className="h-4 w-4 fill-none" />}
              color="red"
              onClick={() => onArchive(field)}
            >
              Archive
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </td>
    </tr>
  )
}

interface CustomFieldsListProps {
  initialOpened?: boolean
}

const CustomFieldsList = ({ initialOpened = false }: CustomFieldsListProps) => {
  const [modalOpened, setModalOpened] = useState(false)
  const [editingField, setEditingField] = useState(null)
  const [archiveModalOpened, setArchiveModalOpened] = useState(false)
  const [fieldToArchive, setFieldToArchive] = useState(null)
  const [optimisticFields, setOptimisticFields] = useState(null)
  const [archivedOpened, setArchivedOpened] = useState(false)
  const [opened, setOpened] = useState(initialOpened)
  const [historyDrawerOpened, setHistoryDrawerOpened] = useState(false)
  const [selectedFieldForHistory, setSelectedFieldForHistory] = useState(null)

  const {
    data: customFieldsData,
    loading,
    refetch,
  } = useQuery(GET_CUSTOM_FIELDS, {
    variables: {
      status: 'ACTIVE',
      pageInfo: {
        limit: 1000, // High limit to fetch all custom fields
        offset: 0,
      },
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })

  const {
    data: archivedFieldsData,
    loading: archivedLoading,
    refetch: refetchArchived,
  } = useQuery(GET_CUSTOM_FIELDS, {
    variables: {
      status: 'ARCHIVED',
      pageInfo: {
        limit: 1000, // High limit to fetch all archived custom fields
        offset: 0,
      },
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })

  // Use optimistic fields during drag, otherwise use query data
  const customFields = optimisticFields || customFieldsData?.customFields?.items || []
  const customFieldsTotalCount = customFieldsData?.customFields?.totalCount || customFields.length
  const archivedFields = archivedFieldsData?.customFields?.items || []
  const archivedTotalCount = archivedFieldsData?.customFields?.totalCount || archivedFields.length

  const [archiveCustomField, { loading: archiving }] = useMutation(
    ARCHIVE_CUSTOM_FIELD,
    {
      onCompleted: () => {
        toast('Custom field archived successfully', 'success')
        refetch()
        refetchArchived()
        setArchiveModalOpened(false)
        setFieldToArchive(null)
      },
      onError: () => toast('Error archiving custom field', 'error'),
    }
  )

  const [reorderCustomFields, { loading: reordering }] = useMutation(
    REORDER_CUSTOM_FIELDS,
    {
      refetchQueries: [
        {
          query: GET_CUSTOM_FIELDS,
          variables: {
            status: 'ACTIVE',
            pageInfo: {
              limit: 1000, // High limit to fetch all custom fields
              offset: 0,
            },
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('Custom fields reordered successfully', 'success')
        // Clear optimistic state after refetch completes
        setOptimisticFields(null)
      },
      onError: () => {
        toast('Error reordering custom fields', 'error')
        // Clear optimistic state on error
        setOptimisticFields(null)
      },
    }
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    // Prevent drag operations while a reorder is in progress
    if (reordering) {
      return
    }

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = customFields.findIndex(
      (field) => field.customFieldId === active.id
    )
    const newIndex = customFields.findIndex(
      (field) => field.customFieldId === over.id
    )

    // Handle case where fields are not found (e.g., data changed during drag)
    if (oldIndex === -1 || newIndex === -1) {
      // Clear optimistic state and refetch to get latest data
      setOptimisticFields(null)
      await refetch()
      toast('The field list has changed. Please try again.', 'error')
      return
    }

    // Calculate new order and update optimistically
    const reorderedFields = arrayMove(customFields, oldIndex, newIndex)
    setOptimisticFields(reorderedFields)

    // Update sort order in database
    const fieldsWithNewOrder = reorderedFields.map((field, index) => ({
      customFieldId: field.customFieldId,
      sortOrder: index,
    }))

    try {
      await reorderCustomFields({
        variables: {
          fields: fieldsWithNewOrder,
        },
      })
    } catch (error) {
      console.error('Error reordering custom fields:', error)
    }
  }

  const handleOpenCreateModal = () => {
    setEditingField(null)
    setModalOpened(true)
    if (!opened) setOpened(true)
  }

  const handleOpenEditModal = (field) => {
    setEditingField(field)
    setModalOpened(true)
  }

  const handleCloseModal = () => {
    setModalOpened(false)
    setEditingField(null)
  }

  const handleOpenArchiveModal = (field) => {
    setFieldToArchive(field)
    setArchiveModalOpened(true)
  }

  const handleCopyId = (customFieldId: string) => {
    navigator.clipboard.writeText(customFieldId)
    toast('Custom Field ID copied to clipboard', 'success')
  }

  const handleConfirmArchive = async () => {
    if (fieldToArchive) {
      await archiveCustomField({
        variables: {
          customFieldId: fieldToArchive.customFieldId,
        },
      })
    }
  }

  const handleShowHistory = (field) => {
    setSelectedFieldForHistory(field)
    setHistoryDrawerOpened(true)
  }

  const isAtLimit = customFieldsTotalCount >= 100

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Custom Fields ({customFieldsTotalCount}/100)
        <Tooltip
          label="You have reached the maximum of 100 custom fields"
          disabled={!isAtLimit}
        >
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              handleOpenCreateModal()
            }}
            disabled={isAtLimit}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconAdd className="h-3.5 w-3.5 fill-none stroke-current" />
            Create
          </DNButton>
        </Tooltip>
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          {/* Loading/Empty/Table States */}
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader color="gray" size="xs" />
            </div>
          ) : customFields.length === 0 ? (
            <div className="flex flex-col items-center gap-4 rounded-md border border-doubleNickel-gray-300 p-8 text-center">
              <div className="text-sm text-doubleNickel-gray-500">
                No custom fields configured yet
              </div>
              <DNButton
                variant="primary"
                size="sm"
                onClick={handleOpenCreateModal}
              >
                Create Your First Custom Field
              </DNButton>
            </div>
          ) : (
            <div className="overflow-auto rounded-md border border-doubleNickel-gray-300">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="w-full">
                  <thead className="bg-doubleNickel-gray-50">
                    <tr className="border-b border-doubleNickel-gray-300">
                      <th className="w-8 px-2 py-2"></th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                        ID
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                        Label
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                        Key
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                        Type
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-doubleNickel-gray-700">
                        Required
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-doubleNickel-gray-700">
                        Searchable
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-doubleNickel-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <SortableContext
                      items={customFields.map((f) => f.customFieldId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {customFields.map((field, index) => (
                        <SortableTableRow
                          key={field.customFieldId}
                          field={field}
                          index={index}
                          totalCount={customFields.length}
                          onEdit={handleOpenEditModal}
                          onArchive={handleOpenArchiveModal}
                          isReordering={reordering}
                          onCopyId={handleCopyId}
                          onShowHistory={handleShowHistory}
                        />
                      ))}
                    </SortableContext>
                  </tbody>
                </table>
              </DndContext>
            </div>
          )}

          {/* Archived Custom Fields Section */}
          <div className="mt-6">
            <button
              onClick={() => setArchivedOpened(!archivedOpened)}
              className="flex w-full items-center justify-between rounded-md border border-doubleNickel-gray-300 bg-doubleNickel-gray-50 px-4 py-3 transition-colors hover:bg-doubleNickel-gray-100"
            >
              <span className="text-sm font-medium text-doubleNickel-gray-700">
                Archived Custom Fields ({archivedTotalCount})
              </span>
              <IconChevronDown
                className={`h-4 w-4 transition-transform ${
                  archivedOpened ? 'rotate-180' : ''
                }`}
              />
            </button>

            <Collapse in={archivedOpened}>
              <div className="mt-2">
                {archivedLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader color="gray" size="xs" />
                  </div>
                ) : archivedFields.length === 0 ? (
                  <div className="rounded-md border border-doubleNickel-gray-300 bg-doubleNickel-gray-50 p-8 text-center">
                    <div className="text-sm text-doubleNickel-gray-500">
                      No archived custom fields
                    </div>
                  </div>
                ) : (
                  <div className="overflow-auto rounded-md border border-doubleNickel-gray-300 bg-doubleNickel-gray-50 opacity-75">
                    <table className="w-full">
                      <thead className="bg-doubleNickel-gray-100">
                        <tr className="border-b border-doubleNickel-gray-300">
                          <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-600">
                            ID
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-600">
                            Label
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-600">
                            Key
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-600">
                            Type
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-doubleNickel-gray-600">
                            Required
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-doubleNickel-gray-600">
                            Searchable
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivedFields.map((field, index) => (
                          <tr
                            key={field.customFieldId}
                            className={`${
                              index !== archivedFields.length - 1
                                ? 'border-b border-doubleNickel-gray-200'
                                : ''
                            }`}
                          >
                            <td className="px-4 py-3">
                              <Tooltip label={field.customFieldId} withArrow>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  color="gray"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCopyId(field.customFieldId)
                                  }}
                                >
                                  <IconCopy className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                                </ActionIcon>
                              </Tooltip>
                            </td>
                            <td className="px-4 py-3 text-sm text-doubleNickel-gray-600">
                              {field.label}
                              {field.options?.length > 0 && (
                                <div className="mt-1 text-xs text-doubleNickel-gray-500">
                                  {field.options.length} option
                                  {field.options.length !== 1 ? 's' : ''}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 font-mono text-sm text-doubleNickel-gray-500">
                              {field.key}
                            </td>
                            <td className="px-4 py-3 text-sm text-doubleNickel-gray-600">
                              {getFieldTypeLabel(field.fieldType)}
                            </td>
                            <td className="px-4 py-3 text-center text-sm">
                              {field.isRequired ? (
                                <span className="text-doubleNickel-gray-500">
                                  ✓
                                </span>
                              ) : (
                                <span className="text-doubleNickel-gray-400">
                                  —
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-sm">
                              {field.isSearchable ? (
                                <span className="text-doubleNickel-gray-500">
                                  ✓
                                </span>
                              ) : (
                                <span className="text-doubleNickel-gray-400">
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Collapse>
          </div>
        </div>
      </Collapse>

      {/* Modals */}
      <CustomFieldModal
        customField={editingField}
        isEdit={!!editingField}
        opened={modalOpened}
        onClose={handleCloseModal}
        onSuccess={() => refetch()}
      />

      <DeleteModal
        title="Archive Custom Field"
        description={`Are you sure you want to archive "${fieldToArchive?.label}"? This field will no longer be available for new applicants, but existing data will be preserved.`}
        opened={archiveModalOpened}
        close={() => {
          setArchiveModalOpened(false)
          setFieldToArchive(null)
        }}
        onDelete={handleConfirmArchive}
        loading={archiving}
      />

      <ChangeHistoryDrawer
        opened={historyDrawerOpened}
        onClose={() => setHistoryDrawerOpened(false)}
        customFieldId={selectedFieldForHistory?.customFieldId}
        fieldLabel={selectedFieldForHistory?.label}
      />
    </div>
  )
}

export default CustomFieldsList
